import { useState } from "react";
import { MapPin, Clock, DollarSign, Truck, Loader2, ArrowRight, Plus, X, Star, Route } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

const RouteCalculator = ({ onEstimate }) => {
  const [stops, setStops] = useState(["", ""]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("simple"); // simple | multi

  const updateStop = (idx, val) => {
    const next = [...stops];
    next[idx] = val;
    setStops(next);
  };

  const addStop = () => {
    if (stops.length < 10) setStops([...stops, ""]);
  };

  const removeStop = (idx) => {
    if (stops.length <= 2) return;
    setStops(stops.filter((_, i) => i !== idx));
  };

  const calculate = async () => {
    const validStops = stops.filter(Boolean);
    if (validStops.length < 2) return;
    setLoading(true);
    setResult(null);
    try {
      if (validStops.length === 2) {
        const res = await api.get(`/transport/estimate?from_state=${validStops[0]}&to_state=${validStops[1]}`);
        setResult({ type: "simple", data: res.data });
        onEstimate?.(res.data);
      } else {
        const res = await api.post("/transport/route-plan", { stops: validStops });
        setResult({ type: "multi", data: res.data });
        onEstimate?.(res.data);
      }
    } catch (err) {
      logger.error("Route calculation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const isValid = stops.filter(Boolean).length >= 2;

  return (
    <div className="glass-card p-5" data-testid="route-calculator">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg text-[#EDEDED] flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#D4AF37]" /> Route Calculator
        </h3>
        <button
          onClick={() => {
            if (mode === "simple") {
              setMode("multi");
              if (stops.length < 3) setStops([...stops, ""]);
            } else {
              setMode("simple");
              setStops(stops.slice(0, 2));
            }
            setResult(null);
          }}
          className="text-xs px-3 py-1 rounded-full border border-[#27272A] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-colors"
          data-testid="toggle-multi-stop"
        >
          {mode === "simple" ? (
            <span className="flex items-center gap-1"><Route className="w-3 h-3" /> Multi-Stop</span>
          ) : (
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Simple</span>
          )}
        </button>
      </div>

      {/* Stops */}
      <div className="space-y-2 mb-4">
        {stops.map((stop, idx) => (
          <div key={`stop-${idx}`} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-[#D4AF37]/10 text-[#D4AF37] flex-shrink-0">
              {idx === 0 ? "A" : idx === stops.length - 1 ? String.fromCharCode(65 + idx) : String.fromCharCode(65 + idx)}
            </div>
            <select
              value={stop}
              onChange={(e) => updateStop(idx, e.target.value)}
              className="input-minimal flex-1"
              data-testid={`stop-${idx}`}
            >
              <option value="">{idx === 0 ? "Origin state..." : idx === stops.length - 1 ? "Destination state..." : "Stop state..."}</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {mode === "multi" && stops.length > 2 && idx > 0 && idx < stops.length - 1 && (
              <button
                onClick={() => removeStop(idx)}
                className="p-1 text-[#71717A] hover:text-red-400 transition-colors"
                data-testid={`remove-stop-${idx}`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {mode === "multi" && stops.length < 10 && (
          <button
            onClick={addStop}
            className="flex-1 py-2 text-xs text-[#A1A1AA] border border-dashed border-[#27272A] rounded-lg hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-1"
            data-testid="add-stop-btn"
          >
            <Plus className="w-3 h-3" /> Add Stop
          </button>
        )}
        <button
          onClick={calculate}
          disabled={loading || !isValid}
          className="flex-1 btn-gold text-sm py-2 disabled:opacity-50"
          data-testid="calculate-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Calculate Route"}
        </button>
      </div>

      {/* Simple Result */}
      {result?.type === "simple" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 mt-4 border-t border-white/5" data-testid="route-result">
          <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
            <MapPin className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
            <p className="text-lg text-[#EDEDED] font-bold">{result.data.estimated_distance_miles.toLocaleString()}</p>
            <p className="text-xs text-[#71717A]">miles</p>
          </div>
          <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
            <Clock className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
            <p className="text-lg text-[#EDEDED] font-bold">{result.data.estimated_transit_days}</p>
            <p className="text-xs text-[#71717A]">day{result.data.estimated_transit_days !== 1 ? "s" : ""}</p>
          </div>
          <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
            <DollarSign className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
            <p className="text-lg text-[#EDEDED] font-bold">${result.data.estimated_cost_low.toLocaleString()}</p>
            <p className="text-xs text-[#71717A]">est. low</p>
          </div>
          <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
            <DollarSign className="w-4 h-4 text-[#22C55E] mx-auto mb-1" />
            <p className="text-lg text-[#EDEDED] font-bold">${result.data.estimated_cost_high.toLocaleString()}</p>
            <p className="text-xs text-[#71717A]">est. high</p>
          </div>
          {result.data.available_transporters > 0 && (
            <div className="col-span-full text-center mt-1">
              <p className="text-xs text-[#D4AF37]">
                <Truck className="w-3 h-3 inline mr-1" />
                {result.data.available_transporters} transporter{result.data.available_transporters > 1 ? "s" : ""} available for this route
              </p>
            </div>
          )}
        </div>
      )}

      {/* Multi-Stop Result */}
      {result?.type === "multi" && (
        <div className="pt-4 mt-4 border-t border-white/5 space-y-4" data-testid="route-plan-result">
          {/* Route Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
              <p className="text-lg text-[#EDEDED] font-bold">{result.data.total_distance_miles.toLocaleString()}</p>
              <p className="text-xs text-[#71717A]">total miles</p>
            </div>
            <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
              <p className="text-lg text-[#EDEDED] font-bold">{result.data.total_transit_days}</p>
              <p className="text-xs text-[#71717A]">transit days</p>
            </div>
            <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
              <p className="text-lg text-[#D4AF37] font-bold">${result.data.estimated_cost_low} - ${result.data.estimated_cost_high}</p>
              <p className="text-xs text-[#71717A]">est. cost</p>
            </div>
          </div>

          {/* Legs */}
          <div>
            <p className="text-xs text-[#71717A] mb-2 uppercase tracking-wider">Route Legs</p>
            <div className="space-y-1">
              {result.data.legs.map((leg, idx) => (
                <div key={`${leg.from}-${leg.to}`} className="flex items-center gap-2 text-sm p-2 bg-[#0A0A0A] rounded">
                  <span className="text-[#D4AF37] font-mono w-8">{leg.from}</span>
                  <ArrowRight className="w-3 h-3 text-[#3F3F46]" />
                  <span className="text-[#D4AF37] font-mono w-8">{leg.to}</span>
                  <span className="text-[#A1A1AA] ml-auto">{leg.distance_miles.toLocaleString()} mi</span>
                  <span className="text-[#71717A]">{leg.transit_days}d</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Transporters */}
          {result.data.recommended_transporters?.length > 0 && (
            <div>
              <p className="text-xs text-[#71717A] mb-2 uppercase tracking-wider">Top Matched Transporters</p>
              <div className="space-y-2">
                {result.data.recommended_transporters.slice(0, 5).map((t) => (
                  <Link
                    key={t.transporter_id}
                    to={`/transporters/${t.transporter_id}`}
                    className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg hover:bg-[#18181B] transition-colors"
                    data-testid={`matched-transporter-${t.transporter_id}`}
                  >
                    <Truck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#EDEDED] truncate">{t.business_name}</p>
                      <div className="flex items-center gap-2 text-xs text-[#71717A]">
                        <span>{t.coverage_pct}% route coverage</span>
                        {t.review_count > 0 && (
                          <span className="flex items-center gap-0.5">
                            <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" /> {t.avg_rating} ({t.review_count})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm text-[#D4AF37] font-semibold">${t.estimated_cost.toLocaleString()}</p>
                      <p className="text-xs text-[#71717A]">${t.rate_per_mile}/mi</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RouteCalculator;
